import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  changePasswordAsync,
  initialAsync,
  postalCodeAsync,
} from "./operations";
import { ProfileState } from "./state";
import { PostalError } from "./types";

const initialState: ProfileState = {
  apps: {
    currentPassword: "",
    newPassword: "",
    newPasswordCheck: "",
    email: "",
    country: "",
    familyName: "",
    firstName: "",
    familyNameFurigana: "",
    firstNameFurigana: "",
    company: "",
    companyFurigana: "",
    office: "",
    department: "",
    position: "",
    jobType: "",
    companyTel: "",
    postalCode: "",
    state: "",
    city: "",
    address: "",
    buildingName: "",
    postalError: "Success",
    market_status: false,
  },
  domains: {},
  error: {},
};

export const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    changeCurrentPassword: (
      state,
      action: PayloadAction<{ currentPassword: string }>
    ) => {
      const { currentPassword } = action.payload;
      state.apps.currentPassword = currentPassword;
    },
    changeNewPassword: (
      state,
      action: PayloadAction<{ newPassword: string }>
    ) => {
      const { newPassword } = action.payload;
      state.apps.newPassword = newPassword;
    },
    changeNewPasswordCheck: (
      state,
      action: PayloadAction<{ passwordCheck: string }>
    ) => {
      const { passwordCheck } = action.payload;
      state.apps.newPasswordCheck = passwordCheck;
    },
    changeCountry: (state, action: PayloadAction<{ country: string }>) => {
      const { country } = action.payload;
      state.apps.country = country;
    },
    changeFamilyName: (
      state,
      action: PayloadAction<{ familyName: string }>
    ) => {
      const { familyName } = action.payload;
      state.apps.familyName = familyName;
    },
    changeFirstName: (state, action: PayloadAction<{ firstName: string }>) => {
      const { firstName } = action.payload;
      state.apps.firstName = firstName;
    },
    changeFamilyNameFurigana: (
      state,
      action: PayloadAction<{ familyNameFurigana: string }>
    ) => {
      const { familyNameFurigana } = action.payload;
      state.apps.familyNameFurigana = familyNameFurigana;
    },
    changeFirstNameFurigana: (
      state,
      action: PayloadAction<{ firstNameFurigana: string }>
    ) => {
      const { firstNameFurigana } = action.payload;
      state.apps.firstNameFurigana = firstNameFurigana;
    },
    changeCompany: (state, action: PayloadAction<{ company: string }>) => {
      const { company } = action.payload;
      state.apps.company = company;
    },
    changeCompanyFurigana: (
      state,
      action: PayloadAction<{ companyFurigana: string }>
    ) => {
      const { companyFurigana } = action.payload;
      state.apps.companyFurigana = companyFurigana;
    },
    changeOffice: (state, action: PayloadAction<{ office: string }>) => {
      const { office } = action.payload;
      state.apps.office = office;
    },
    changeDepartment: (
      state,
      action: PayloadAction<{ department: string }>
    ) => {
      const { department } = action.payload;
      state.apps.department = department;
    },
    changePosition: (state, action: PayloadAction<{ position: string }>) => {
      const { position } = action.payload;
      state.apps.position = position;
    },
    changeJobType: (state, action: PayloadAction<{ jobType: string }>) => {
      const { jobType } = action.payload;
      state.apps.jobType = jobType;
    },
    changeCompanyTel: (
      state,
      action: PayloadAction<{ companyTel: string }>
    ) => {
      const { companyTel } = action.payload;
      state.apps.companyTel = companyTel;
    },
    changePostalCode: (
      state,
      action: PayloadAction<{ postalCode: string }>
    ) => {
      const { postalCode } = action.payload;
      state.apps.postalCode = postalCode;
    },
    changeState: (states, action: PayloadAction<{ state: string }>) => {
      const { state } = action.payload;
      states.apps.state = state;
    },
    changeCity: (state, action: PayloadAction<{ city: string }>) => {
      const { city } = action.payload;
      state.apps.city = city;
    },
    changeAddress: (state, action: PayloadAction<{ address: string }>) => {
      const { address } = action.payload;
      state.apps.address = address;
    },
    changeBuildingName: (
      state,
      action: PayloadAction<{ buildingName: string }>
    ) => {
      const { buildingName } = action.payload;
      state.apps.buildingName = buildingName;
    },
    changeMarket_Status: (
      state,
      action: PayloadAction<{ market_status: boolean }>
    ) => {
      const { market_status } = action.payload;
      state.apps.market_status = market_status;
    },
    changePostalError: (
      state,
      action: PayloadAction<{ postalError: PostalError }>
    ) => {
      const { postalError } = action.payload;
      state.apps.postalError = postalError;
    },
    cleanupByCountyChange: (state) => {
      state.apps.firstNameFurigana = "";
      state.apps.familyNameFurigana = "";
      state.apps.companyFurigana = "";
      state.apps.state = "";
      state.apps.city = "";
      state.apps.address = "";
      state.apps.buildingName = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initialAsync.pending, (state) => {
      state.error = {};
    });
    builder.addCase(initialAsync.fulfilled, (state, action) => {
      const { payload } = action;
      state.apps.email = payload.email;
      state.apps.address = payload.address;
      state.apps.buildingName = payload.buildingName;
      state.apps.city = payload.city;
      state.apps.company = payload.company;
      state.apps.companyFurigana = payload.companyFurigana;
      state.apps.companyTel = payload?.companyTel;
      state.apps.country = payload.country;
      state.apps.department = payload.department;
      state.apps.email = payload.email;
      state.apps.familyName = payload.familyName;
      state.apps.familyNameFurigana = payload.familyNameFurigana;
      state.apps.firstName = payload.givenName;
      state.apps.firstNameFurigana = payload.givenNameFurigana;
      state.apps.jobType = payload.jobType;
      state.apps.office = payload.office;
      state.apps.position = payload.position;
      state.apps.postalCode = payload.postalCode;
      state.apps.state = payload.state;
      state.apps.market_status = payload.market_status;
    });
    builder.addCase(initialAsync.rejected, (state, action) => {
      const { payload } = action;
      state.error = { initialError: payload?.error };
    });
    builder.addCase(changePasswordAsync.pending, (state) => {
      state.error = {};
    });
    builder.addCase(changePasswordAsync.rejected, (state, action) => {
      const { payload } = action;
      state.error = { changePasswordError: payload?.error };
    });
    builder.addCase(postalCodeAsync.pending, (state) => {
      state.error = {};
    });
    builder.addCase(postalCodeAsync.fulfilled, (state, action) => {
      const { payload } = action;
      if (payload?.status === 400) {
        state.apps.postalError = "BadRequest";
        state.apps.state = "";
        state.apps.city = "";
        return;
      }
      if (payload?.status === 500 || payload?.results?.length !== 1) {
        state.apps.postalError = "Error";
        state.apps.state = "";
        state.apps.city = "";
        return;
      }
      state.apps.postalError = "Success";
      state.apps.state = payload.results[0].address1;
      state.apps.city =
        payload.results[0].address2 + payload.results[0].address3;
    });
    builder.addCase(postalCodeAsync.rejected, (state) => {
      state.apps.postalError = "Error";
      state.apps.state = "";
      state.apps.city = "";
    });
  },
});
export const {
  changeNewPassword,
  changeNewPasswordCheck,
  changeCurrentPassword,
  changeCountry,
  changeFamilyName,
  changeFirstName,
  changeFamilyNameFurigana,
  changeFirstNameFurigana,
  changeCompany,
  changeCompanyFurigana,
  changeOffice,
  changeDepartment,
  changePosition,
  changeJobType,
  changeCompanyTel,
  changePostalCode,
  changeState,
  changeCity,
  changeAddress,
  changeBuildingName,
  changeMarket_Status,
  cleanupByCountyChange,
  changePostalError,
} = profileSlice.actions;
export default profileSlice.reducer;
